<template>
  <select
    class="form-select form-select-sm"
    :value="modelValue ?? ''"
    :disabled="disabled || loading"
    :required="required"
    @change="onChange"
  >
    <option value="">{{ loading ? 'Loading…' : (error ? '(failed to load)' : placeholder) }}</option>
    <option v-for="o in options" :key="o.value" :value="o.value">{{ o.label }}</option>
    <!-- Preserve an out-of-list current value (e.g. an archived/foreign id)
         so editing an existing record never silently drops its selection. -->
    <option v-if="orphanValue !== null" :value="orphanValue">#{{ orphanValue }} (unavailable)</option>
    <!-- Opt-in escape hatch: picking this emits `create` instead of a value, so
         a host can offer "make one" without the user leaving to find the list
         first. The select snaps back to its previous selection either way. -->
    <option v-if="createLabel" :value="CREATE_SENTINEL">{{ createLabel }}</option>
  </select>
</template>

<script>
/**
 * MusubiEntitySelect — a labelled <select> backed by a list endpoint, for the
 * many admin forms that previously asked the user to type a raw numeric id
 * (template_id, sender_identity_id, list_id, …). Fetches options once on mount;
 * emits the chosen id via v-model. The host supplies how to label each option.
 *
 *   <MusubiEntitySelect
 *     v-model="form.template_id"
 *     endpoint="/api/v1/branding/templates" response-key="templates"
 *     :option-label="t => t.title" :filter="t => t.surface === 'email'"
 *     placeholder="Select a template…" required />
 */
import useApi from "@apexl/icarus-core/src/services/api";

/** Sentinel option value. Never a real id, so it cannot collide with one. */
const CREATE_SENTINEL = '__create__';

export default {
  name: 'MusubiEntitySelect',
  props: {
    modelValue:  { type: [Number, String], default: null },
    endpoint:    { type: String, required: true },
    /** Key under the JSON payload holding the array (e.g. 'templates'). Blank = auto-detect. */
    responseKey: { type: String, default: '' },
    /** Derive each option's display text from its row. */
    optionLabel: { type: Function, default: (o) => o.name ?? o.title ?? `#${o.id}` },
    /** Field on the row used as the option value. */
    optionValue: { type: String, default: 'id' },
    /** Optional client-side filter (e.g. surface === 'email'). */
    filter:      { type: Function, default: null },
    placeholder: { type: String, default: 'Select…' },
    required:    { type: Boolean, default: false },
    disabled:    { type: Boolean, default: false },
    /** Emit the value as a Number (ids) vs raw string. */
    numeric:     { type: Boolean, default: true },
    /** When set, adds a final option that emits `create` rather than a value. */
    createLabel: { type: String, default: '' },
  },
  emits: ['update:modelValue', 'loaded', 'create'],
  data() {
    return { api: useApi(), rows: [], loading: false, error: null, CREATE_SENTINEL };
  },
  computed: {
    options() {
      const r = this.filter ? this.rows.filter(this.filter) : this.rows;
      return r.map((o) => ({ value: o[this.optionValue], label: String(this.optionLabel(o) ?? `#${o[this.optionValue]}`) }));
    },
    orphanValue() {
      const v = this.modelValue;
      if (v === null || v === undefined || v === '') return null;
      return this.options.some((o) => String(o.value) === String(v)) ? null : v;
    },
  },
  mounted() { this.load(); },
  methods: {
    async load() {
      this.loading = true; this.error = null;
      try {
        const res = await this.api.get(this.endpoint);
        const payload = res?.data ?? res;
        let rows;
        if (this.responseKey) {
          rows = payload?.[this.responseKey] ?? payload?.data?.[this.responseKey] ?? [];
        } else {
          rows = payload?.data ?? payload?.rows ?? payload;
        }
        this.rows = Array.isArray(rows) ? rows : [];
        this.$emit('loaded', this.rows);
      } catch (e) {
        this.error = e?.message || 'load failed';
        this.rows = [];
      }
      this.loading = false;
    },
    onChange(e) {
      const raw = e.target.value;
      // Intercept BEFORE the Number() cast below, which would turn the sentinel
      // into NaN and quietly wipe the field.
      if (raw === CREATE_SENTINEL) {
        e.target.value = this.modelValue ?? '';
        this.$emit('create');
        return;
      }
      if (raw === '') { this.$emit('update:modelValue', null); return; }
      this.$emit('update:modelValue', this.numeric ? Number(raw) : raw);
    },
  },
};
</script>
